import React, { useState } from "react";
import { AddToCart } from "./AddToCart";
import { ScentSelector } from "./ScentSelector";
import { PdpTab } from "./PdpTab";
import {
  usePdpFocusedScent,
  usePdpScents,
  usePdpProduct,
  usePdpSellingPlan,
  usePdpVariant,
} from "./PdpContext";
import { Button } from "@/components/Button";
import { getFinalAtcCtaPrice } from "@/utils/atcCTAPrice";
import { DroppsVariant, variantSelect, productSelect } from "@/store";
import {
  MoneyBackIcon,
  FreeShippingIcon,
  DollarCircleIcon,
  FreeShippingTrialProductIcon,
  TagIcon,
} from "@/components/icons";
import { formatPerPod } from "@/helpers/perPodPrice";

import { RecommendedProducts } from "./RecommendedProducts";
export interface CartFlowProps {
  cartAddId: string;
}

function getQuantityDiscountMessage(
  quantity_discount: number,
  default_variant_multiplier: number,
  highestMultiplier: number,
  lowestMultiplier: number,
  isBundle: boolean
) {
  if (typeof default_variant_multiplier === "number") {
    if (isBundle) {
      // Render the original message for the highest multiplier
      return (
        <li className="mt-1 flex items-center gap-1 text-[13px] lg:text-sm">
          <TagIcon sx={{ width: 14, height: 14 }} />
          <span className="font-bold">{quantity_discount}% OFF</span> for
          bundling up
        </li>
      );
    }

    if (default_variant_multiplier === highestMultiplier) {
      // Render the original message for the highest multiplier
      return (
        <li className="mt-1 flex items-center gap-1 text-[13px] lg:text-sm">
          <TagIcon sx={{ width: 14, height: 14 }} />
          <span className="font-bold">{quantity_discount}% OFF</span> for
          getting a larger box size
        </li>
      );
    } else if (default_variant_multiplier === lowestMultiplier) {
      // No message for the lowest multiplier
      // return null;
    } else {
      // Message for the middle case
      return (
        <li className="mt-1 flex items-center gap-1 text-[13px]">
          <TagIcon sx={{ width: 14, height: 14 }} />
          <span className="font-bold">{quantity_discount}% OFF</span> for
          getting a larger box size
        </li>
      );
    }
  }

  // Fallback in case default_variant_multiplier is undefined or not a number
}

// Helper function to get the highest multiplier
function getHighestMultiplier(variants: DroppsVariant[]): number {
  return Math.max(
    ...variants.map(
      (variant) => variant.metafields?.default_variant_multiplier || 0
    )
  );
}

// Helper function to get the lowest multiplier
function getLowestMultiplier(variants: DroppsVariant[]): number {
  return Math.min(
    ...variants.map(
      (variant) => variant.metafields?.default_variant_multiplier || 1
    )
  );
}

function CartFlow({ cartAddId }: CartFlowProps) {
  const focusedScent = usePdpFocusedScent();
  const { pdpTabData, pdpIngredientSection, tags } = usePdpProduct();
  const scents = usePdpScents();
  const [toggleScentOpen, setToggleScentOpen] = useState(false);
  const product = usePdpProduct();
  const variant = usePdpVariant();
  const sellingPlan = usePdpSellingPlan();
  const subscription = variantSelect.orderableSubscription(variant);
  const isBulk = product.title.toLocaleLowerCase().includes("bulk");
  const isBundle = product?.type === "Bundle";
  const isTrialProduct = tags?.includes("trial-product");
  const highestMultiplier = getHighestMultiplier(product.variants);
  const lowestMultiplier = getLowestMultiplier(product.variants);
  const onetimeDisplayPrice = variantSelect.onetimePrice(variant, product);
  const subscriptionDisplayPrice = variantSelect.lowestPrice(variant, product);

  const productPrice = getFinalAtcCtaPrice({
    productCollections: product.collections,
    basePriceCents: sellingPlan
      ? subscriptionDisplayPrice.asCents()
      : onetimeDisplayPrice.asCents(),
    sellingPlan,
    variant,
    product,
    currency: "$",
    strikeThroughSize: "text-base",
    priceSize: "text-xl",
    strikeThroughColor: "text-primary",
    priceColor: "text-primary",
  });

  return (
    <div className="grid w-full gap-4">
      {
        <div>
          {focusedScent !== undefined && (
            <div className="relative -mx-4 mb-2 w-screen md:-mx-6 lg:mx-0 lg:mb-4 lg:w-auto">
              <ScentSelector
                focusedScent={focusedScent}
                showBottomPart={false}
                showScentNameByDefault={true}
                enableRedirectWhenOOS={true}
              />
            </div>
          )}
          <div className="flex items-center space-x-2">
            <span className="text-[28px] font-bold leading-tight text-black">
              {productPrice.currentPrice}
            </span>
            {(() => {
              const perPodText = formatPerPod({
                displayPriceCents:
                  sellingPlan && subscription
                    ? subscriptionDisplayPrice.asCents()
                    : onetimeDisplayPrice.asCents(),
                packageQuantity: variant?.package_quantity ?? 0,
                productType: product?.type,
                isBulk,
              });
              return perPodText ? (
                <span className="text-[13px] text-black lg:text-xs">
                  ({perPodText})
                </span>
              ) : null;
            })()}
          </div>
          {product?.type != "Bundle" &&
            (variant.compareAtPrice &&
            variant.compareAtPrice.asCents() > variant.price.asCents() ? (
              <div className="mt-2 text-[13px] leading-tight text-black lg:text-xs">
                List price:{" "}
                <span className="line-through">
                  {variant.compareAtPrice.format()}
                </span>
              </div>
            ) : sellingPlan &&
              productPrice.currentPrice != onetimeDisplayPrice.format() ? (
              <div className="mt-2 text-[13px] leading-tight text-black lg:text-xs">
                List price:{" "}
                <span className="line-through">
                  {onetimeDisplayPrice.format()}
                </span>
              </div>
            ) : null)}
          {product?.type === "Bundle" && variant.compareAtPrice && (
            <div className="mt-2 text-[13px] leading-tight text-black lg:text-xs">
              List price:{" "}
              <span className="line-through">
                {variant.compareAtPrice.format()}
              </span>
            </div>
          )}

          {typeof variant?.quantity_discount === "number" &&
            variant.quantity_discount > 0 &&
            getQuantityDiscountMessage(
              variant.quantity_discount,
              variant.default_variant_multiplier,
              highestMultiplier,
              lowestMultiplier,
              product?.type === "Bundle"
            )}
        </div>
      }

      <AddToCart cartSubmitId={cartAddId} />

      <RecommendedProducts />

      {pdpTabData &&
        (pdpTabData.overview ||
          pdpTabData.impact ||
          pdpTabData.ingredients) && (
          <PdpTab
            tabData={pdpTabData}
            ingredientTabDrawerData={pdpIngredientSection}
          />
        )}
    </div>
  );
}

export default CartFlow;
